import { ProcessedTripItinerary, TourOperator } from "../contentfulModel.ts";
import React from "react";

interface PricingRow {
    concept: string;
    values: string[];
}

interface PricingCategory {
    seasons: string[];
    pricings: PricingRow[];
}

interface ItineraryPricingPDFProps {
    tourPackage: ProcessedTripItinerary;
    tourOperator: TourOperator;
}

// Layout constants – A3 landscape at 96 dpi (1 cm ≈ 37.795 px)
const PAGE_HEIGHT_PX = 29.71 * 37.795; // ≈ 1122 px
const HERO_HEIGHT_PX = 320;
const CONTENT_PADDING_V = 48;  // py-6: 24 px × 2
const TITLE_AREA_H = 56;       // title text + mb-2 + gap-4

const AVAILABLE_GRID_H = PAGE_HEIGHT_PX - HERO_HEIGHT_PX - CONTENT_PADDING_V - TITLE_AREA_H;

// Estimate rendered height of one category table (compact layout)
function estimateCategoryH(data: PricingCategory): number {
    const CATEGORY_HEADER = 44; // px-6 py-3 + h2
    const TABLE_HEADER = 36;    // thead row
    const DATA_ROW = 26;        // tbody row with py-1.5
    return CATEGORY_HEADER + TABLE_HEADER + data.pricings.length * DATA_ROW;
}

// Return a CSS scale < 1 when the tallest table in the chunk would overflow
function getGridScale(chunk: [string, PricingCategory][]): number {
    const maxH = Math.max(...chunk.map(([, data]) => estimateCategoryH(data)));
    return maxH > AVAILABLE_GRID_H ? AVAILABLE_GRID_H / maxH : 1;
}

export const ItineraryPricingPDF: React.FC<ItineraryPricingPDFProps> = ({
    tourPackage,
    tourOperator,
}) => {
    const pricingData = (tourPackage.pricing || {}) as Record<string, PricingCategory>;
    const pricingEntries = Object.entries(pricingData) as [string, PricingCategory][];

    const chunkSize = 2;
    const pricingChunks: [string, PricingCategory][][] = [];
    for (let i = 0; i < pricingEntries.length; i += chunkSize) {
        pricingChunks.push(pricingEntries.slice(i, i + chunkSize));
    }

    return (
        <>
            {pricingChunks.map((chunk, pageIndex) => {
                const scale = getGridScale(chunk);

                return (
                    <div key={pageIndex} className="flex flex-col bg-white mb-8" style={{
                        aspectRatio: 1.4136,
                        height: "29.71cm",
                        width: "42cm",
                        pageBreakAfter: "always",
                        overflow: "hidden",
                    }}>
                        {/* Hero Image */}
                        <div className="w-full flex-none" style={{
                            height: `${HERO_HEIGHT_PX}px`,
                            backgroundImage: `url(${tourPackage?.mainImage.file?.includes(" ") ? encodeURI(tourPackage.mainImage.file ?? "") : tourPackage?.mainImage.file})`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                        }}>
                            <div className="text-white p-10 py-6 h-full flex flex-col w-full justify-between">
                                <div className="flex flex-row justify-between">
                                    <div className="text-lg font-light">{tourPackage.name}</div>
                                    <div className="uppercase text-lg font-light" style={{ color: "white" }}>
                                        {tourOperator.name}
                                    </div>
                                </div>
                                <div className="flex items-center justify-end gap-4">
                                    <div className="rounded-full px-6 py-4 font-medium" style={{
                                        color: tourOperator.primaryColor || "#000000",
                                        fontSize: "22pt",
                                        lineHeight: "22pt",
                                        background: "rgba(255, 255, 255, 0.9)",
                                    }}>
                                        {tourPackage.itinerary.length - 1} NOCHES
                                    </div>
                                    {tourPackage.price && (
                                        <div className="rounded-full px-6 py-2 font-medium" style={{
                                            color: tourOperator.primaryColor || "#000000",
                                            fontSize: "20pt",
                                            lineHeight: "18pt",
                                            background: "rgba(255, 255, 255, 0.9)",
                                        }}>
                                            <div className="text-[10pt] uppercase font-light">Desde</div>
                                            <div>{tourPackage.price}</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Content: title + scaled table grid */}
                        <div className="flex-1 min-h-0 overflow-hidden px-10 py-6 flex flex-col gap-4">
                            <div className="font-bold text-2xl text-start flex-none" style={{
                                color: tourOperator.primaryColor || "#000000",
                            }}>
                                Tarifas por Categoría{pricingChunks.length > 1 ? ` (${pageIndex + 1}/${pricingChunks.length})` : ""}
                            </div>

                            {/* Scale wrapper – shrinks the grid to fit when there are many rows */}
                            <div style={{
                                transformOrigin: "top left",
                                transform: scale < 1 ? `scale(${scale.toFixed(4)})` : undefined,
                                width: scale < 1 ? `${(100 / scale).toFixed(2)}%` : "100%",
                            }}>
                                <div className={`grid ${chunk.length === 2 ? "grid-cols-2" : "grid-cols-1"} gap-4 items-start`}>
                                    {chunk.map(([categoryName, data], index) => (
                                        <div key={index} className="flex flex-col rounded-2xl overflow-hidden border border-neutral-400 bg-white">
                                            {/* Category header */}
                                            <div className="px-6 py-3" style={{ backgroundColor: tourOperator.secondaryColor || "#4b5563" }}>
                                                <h2 className="text-white font-bold uppercase tracking-wider text-sm">
                                                    {categoryName}
                                                </h2>
                                            </div>

                                            <table className="w-full text-left text-[9pt] border-collapse">
                                                <thead>
                                                <tr className="bg-slate-50 border-b border-slate-100">
                                                    <th className="px-3 py-2 text-slate-500 font-medium text-[8pt]">Concepto</th>
                                                    {data.seasons.map((season, sIdx) => (
                                                        <th key={sIdx} className="px-3 py-2 text-slate-900 font-semibold text-center text-[8pt] leading-tight">
                                                            {season}
                                                        </th>
                                                    ))}
                                                </tr>
                                                </thead>
                                                <tbody className="divide-y divide-slate-100">
                                                {data.pricings.map((priceRow, pIdx) => (
                                                    <tr key={pIdx} className={pIdx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                                                        <td className="px-3 py-1.5 font-semibold text-slate-700 text-[8pt] w-[120px]">
                                                            {priceRow.concept}
                                                        </td>
                                                        {priceRow.values.map((value, vIdx) => (
                                                            <td key={vIdx} className="px-2 py-1.5 text-center text-slate-800 font-medium text-[9pt] whitespace-nowrap">
                                                                {value}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </>
    );
};
